"use client";

import { motion } from "motion/react";

export function IterationSection() {
  return (
    <section className="relative flex h-full w-full flex-col justify-center bg-white py-20 lg:py-32">
      <div className="mx-auto w-full max-w-[1400px] px-6 lg:px-8">
        {/* Header Row */}
        <div className="mb-24 grid w-full grid-cols-1 gap-12 pb-8 text-center text-4xl font-semibold tracking-tight text-black md:grid-cols-3 md:gap-8 lg:text-6xl">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex justify-center"
          >
            Iterate
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex justify-center"
          >
            Towards
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex justify-center"
          >
            Excellence
          </motion.div>
        </div>

        {/* Central Description */}
        <div className="mb-24 text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mx-auto max-w-2xl text-lg leading-relaxed text-black lg:text-xl"
          >
            You can't one-shot finance transformation. CoPlane allows teams to
            start small, iterate fast, and compound value over time. Start with
            an agent in the chaos. Use your historical decisions as your evaluation criteria.
            Apply feedback, iterate, add guardrails and watch it self-assemble
            into a workflow. Then connect the rest of the business.
          </motion.p>
        </div>

        {/* Steps */}
        <div className="grid gap-12 md:grid-cols-3 md:gap-8">
          <IterationStep
            number="1"
            title="Start Small"
            description="Don't over-engineer. Give an agent your SOP, tools, and let it work. It learns the contours of your messy, real-world data faster than any consultant."
            delay={0.4}
            arrowCount={1}
          />

          <IterationStep
            number="2"
            title="Iterate Fast"
            description="Two-way match? Make it three. Sampling? Connect every line item. As the workflow self-assembles, you turn the chaos into mere complexity. Build the process you actually need."
            delay={0.5}
            arrowCount={2}
          />

          <IterationStep
            number="3"
            title="Scale Confidently."
            description="Expand link by link. Connect the EDI mailbox. Orchestrate adaptive keystrokes in your AS/400. Reconcile revenue. Apply cash. Turn your static back office into an adaptive, agentic engine."
            delay={0.6}
            arrowCount={3}
          />
        </div>
      </div>
    </section>
  );
}

function IterationStep({
  number,
  title,
  description,
  delay = 0,
  arrowCount = 1
}: {
  number: string;
  title: string;
  description: string;
  delay?: number;
  arrowCount: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay }}
      className="flex flex-col"
    >
      {/* Top Rail */}
      <PerspectiveRail inverted />

      <div className="pt-8 pb-8">
        {/* Number + Arrows */}
        <div className="mb-6 flex items-center gap-4">
           <div className="flex h-10 w-10 items-center justify-center rounded-full bg-black text-white font-bold text-lg">
             {number}
           </div>
           <div className="flex gap-1">
             {Array.from({ length: arrowCount }).map((_, i) => (
                <TriangleArrow key={i} />
             ))}
           </div>
        </div>

        <h3 className="mb-4 text-2xl font-bold text-black">{title}</h3>
        <p className="mb-8 text-lg leading-relaxed text-black/80">{description}</p>
      </div>

      {/* Bottom Rail */}
      <div className="mt-auto">
        <PerspectiveRail />
      </div>
    </motion.div>
  );
}

function PerspectiveRail({ inverted = false }: { inverted?: boolean }) {
  return (
    <div className="relative h-[20px] w-full" style={{ perspective: "200px" }}>
      <div
        className="absolute inset-x-0 top-1/2 h-[12px] -translate-y-1/2 bg-black"
        style={{
          transform: `rotateX(${inverted ? "-45deg" : "45deg"})`,
          borderRadius: "1px",
        }}
      />
    </div>
  );
}

function TriangleArrow() {
  return (
     <svg 
       width="24" 
       height="24" 
       viewBox="0 0 24 24" 
       fill="currentColor" 
       className="text-black w-8 h-8" // matched size to screenshot
     >
       <path d="M8 5v14l11-7z" />
     </svg>
  );
}
